import { inject, Injectable, Type } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AlertDialogComponent } from './alert-dialog/alert-dialog.component';
import { ConfirmDialogComponent } from './confirm-dialog/confirm-dialog.component';
import { DIALOG_DEFAULT_CONFIG } from './dialog-tokens';
import {
  AlertDialogData,
  ConfirmDialogData,
  DialogConfigBase,
  PromptDialogData,
  SelectDialogData,
} from './dialog.model';
import { PromptDialogComponent } from './prompt-dialog/prompt-dialog.component';
import { SelectDialogComponent } from './select-dialog/select-dialog.component';

/**
 * A service for opening alert, confirm and prompt dialogs.
 */
@Injectable({
  providedIn: 'root',
})
export class DialogService {
  private readonly _dialog = inject(MatDialog);
  private readonly _defaultConfig = inject(DIALOG_DEFAULT_CONFIG);

  /**
   * Trigger an alert dialog
   * @param data The data to pass to the dialog
   * @param config Extra configuration options.
   * @returns Reference to the dialog
   */
  alert(data: AlertDialogData, config?: DialogConfigBase) {
    const cfg = this._mergeConfigWith(data, this._applyDefaultConfig(config));

    return this._dialog.open(AlertDialogComponent, cfg);
  }

  /**
   * Trigger an confirm dialog
   * @param data The data to pass to the dialog
   * @param config Extra configuration options.
   * @returns Reference to the dialog
   */
  confirm(data: ConfirmDialogData, config?: DialogConfigBase) {
    const cfg = this._mergeConfigWith(data, this._applyDefaultConfig(config));

    return this._dialog.open(ConfirmDialogComponent, cfg);
  }

  /**
   * Trigger an prompt dialog
   * @param data The data to pass to the dialog
   * @param config Extra configuration options.
   * @returns Reference to the dialog
   */
  prompt(data: PromptDialogData, config?: DialogConfigBase) {
    const cfg = this._mergeConfigWith(data, this._applyDefaultConfig(config));

    return this._dialog.open(PromptDialogComponent, cfg);
  }

  /**
   * Trigger an select dialog
   * @param data The data to pass to the dialog
   * @param config Extra configuration options.
   * @returns Reference to the dialog
   */
  select<T>(data: SelectDialogData<T>, config?: DialogConfigBase) {
    const cfg = this._mergeConfigWith(data, this._applyDefaultConfig(config));

    return this._dialog.open(SelectDialogComponent, cfg);
  }

  open<T, D = unknown>(component: Type<T>, data: D, config?: DialogConfigBase) {
    const cfg = this._mergeConfigWith(data, this._applyDefaultConfig(config));

    return this._dialog.open(component, cfg);
  }

  private _mergeConfigWith<T>(
    data: AlertDialogData | ConfirmDialogData | PromptDialogData | T,
    config?: DialogConfigBase
  ) {
    return {
      data,
      ...config,
    };
  }

  private _applyDefaultConfig(config?: DialogConfigBase) {
    return {
      ...this._defaultConfig,
      ...config,
    };
  }
}
